type ScoreFilterId = string | number;
type ScoreFilterValue = ScoreFilterId | ScoreFilterId[] | null | undefined;

export const hasValidCurrentScore = <T extends { currentScore?: unknown }>(
    scoreData: T | [] | null | undefined
): scoreData is T =>
    typeof scoreData === 'object' &&
    scoreData !== null &&
    'currentScore' in scoreData &&
    typeof scoreData.currentScore === 'number' &&
    Number.isFinite(scoreData.currentScore);

export const getAvailableScoreData = <T extends { currentScore?: unknown }>(
    liveScore: T | [] | null | undefined,
    savedScore: T | null | undefined
): T | undefined => {
    if (hasValidCurrentScore(liveScore)) return liveScore ?? undefined;
    return hasValidCurrentScore(savedScore) ? savedScore ?? undefined : undefined;
};

const getFirstScoreFilterId = (value: ScoreFilterValue): ScoreFilterId | undefined => {
    const id = Array.isArray(value) ? value[0] : value;
    return id !== undefined && id !== null && id !== '' ? id : undefined;
};

/**
 * Builds mutually exclusive score query params. Array inputs are supported for
 * compatibility with widget filters, but only the first selected ID is sent.
 */
export const getScoreQueryParams = (
    deviceId: ScoreFilterValue,
    groupId: ScoreFilterValue
): Partial<Record<'deviceId' | 'groupId', ScoreFilterId>> => {
    const selectedDeviceId = getFirstScoreFilterId(deviceId);
    if (selectedDeviceId !== undefined) return { deviceId: selectedDeviceId };

    const selectedGroupId = getFirstScoreFilterId(groupId);
    return selectedGroupId !== undefined ? { groupId: selectedGroupId } : {};
};

export const getWidgetScoreQueryParams = (
    filters: { deviceId?: ScoreFilterValue; groupId?: ScoreFilterValue } | undefined,
    deviceId: ScoreFilterValue,
    groupId: ScoreFilterValue
): Partial<Record<'deviceId' | 'groupId', ScoreFilterId>> => {
    const configuredParams = getScoreQueryParams(filters?.deviceId, filters?.groupId);
    return Object.keys(configuredParams).length ? configuredParams : getScoreQueryParams(deviceId, groupId);
};
